import { FC, useState } from "react";
import { Tabs, Button, Tooltip, Progress } from "antd";
import {
  Rocket,
  Users,
  Calendar,
  Anchor,
  ListTodo,
  Briefcase,
  Settings
} from "lucide-react";
import { ProjectSelector } from "./components/project-overview/project-selector";
import { StatsDashboard } from "./components/project-overview/stats-dashboard";
import { BookingsServicesView } from "./components/bookings-services/bookings-services-view";
import { NotesPanel } from "./components/tasks-notes/notes-panel";
import { ProjectScheduler } from "./components/project-scheduler";
import { ProjectSettingsModal } from "./components/project-settings-modal";
import { MasterStaffing } from "./components/master-staffing/master-staffing";
import { useMissionControlStore } from "./mission-control.store";

type Props = {};

export const MissionControl: FC<Props> = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const { selectedProjectId, isLoading } = useMissionControlStore();

  const tabs = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <Rocket size={16} />
          Project Overview
        </span>
      ),
      children: (
        <div className="p-6">
          <StatsDashboard />
        </div>
      ),
    },
    {
      key: "bookings",
      label: (
        <span className="flex items-center gap-2">
          <Briefcase size={16} />
          Bookings & Services
        </span>
      ),
      children: (
        <div className="p-6">
          {selectedProjectId ? (
            <BookingsServicesView />
          ) : (
            <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
              <Briefcase className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No Project Selected
              </h3>
              <p className="text-gray-500">
                Select a project in the Overview tab to view bookings and services
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "staffing",
      label: (
        <span className="flex items-center gap-2">
          <Users size={16} />
          Master Staffing
        </span>
      ),
      children: (
        <div className="p-6">
          {selectedProjectId ? (
            <MasterStaffing projectId={selectedProjectId} />
          ) : (
            <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No Project Selected
              </h3>
              <p className="text-gray-500">
                Select a project in the Overview tab to access master staffing
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "scheduler",
      label: (
        <span className="flex items-center gap-2">
          <Calendar size={16} />
          Project Scheduler
        </span>
      ),
      children: (
        <div className="p-6">
          {selectedProjectId ? (
            <ProjectScheduler />
          ) : (
            <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
              <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No Project Selected
              </h3>
              <p className="text-gray-500">
                Select a project in the Overview tab to view the project scheduler
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "dock",
      label: (
        <span className="flex items-center gap-2">
          <Anchor size={16} />
          Dock Management
        </span>
      ),
      children: (
        <div className="p-6">
          {selectedProjectId ? (
            <>
              <h2 className="text-xl font-semibold mb-4">Dock Management</h2>
              <p className="text-gray-600">
                Email shippers, manage booking slots, receive inventory lists, and confirm arrivals.
              </p>
            </>
          ) : (
            <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
              <Anchor className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No Project Selected
              </h3>
              <p className="text-gray-500">
                Select a project in the Overview tab to access dock management
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "tasks",
      label: (
        <span className="flex items-center gap-2">
          <ListTodo size={16} />
          Tasks & Notes
        </span>
      ),
      children: (
        <div className="p-6">
          {selectedProjectId ? (
            <NotesPanel />
          ) : (
            <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
              <ListTodo className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No Project Selected
              </h3>
              <p className="text-gray-500">
                Select a project in the Overview tab to manage tasks and notes
              </p>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="h-full bg-gray-50 relative">
      {isLoading && (
        <Progress
          percent={100}
          status="active"
          showInfo={false}
          strokeColor="#1890ff"
          className="absolute top-0 left-0 right-0 z-50"
          style={{ marginBottom: 0 }}
          size="small"
        />
      )}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Rocket className="text-blue-600" size={24} />
            <div>
              <h1 className="text-xl font-bold">Mission Control</h1>
              <p className="text-xs text-gray-500">Operational project management hub</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {selectedProjectId && (
              <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-1">
                <p className="text-xs text-blue-600 font-medium">
                  {useMissionControlStore.getState().selectedProject?.name}
                </p>
              </div>
            )}
            <div className="min-w-[300px]">
              <ProjectSelector />
            </div>
            {selectedProjectId && (
              <Tooltip title="Project Settings">
                <Button
                  icon={<Settings size={16} />}
                  onClick={() => setSettingsModalVisible(true)}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          className="mission-control-tabs"
        />
      </div>

      <ProjectSettingsModal
        visible={settingsModalVisible}
        onClose={() => setSettingsModalVisible(false)}
      />
    </div>
  );
};
